/**
 * Phone Chat (docs/mobile-design.md §4.6), shared by mobile web and React Native: the chat list (latest activity
 * first, segments), the thread line, the long-press action sheet, the composer's keyboard inset, which chat screens are
 * full screen, and when an unsent message is retried by itself (offline plan step 1, docs/offline.md). Pure TS.
 */
import type { Conversation } from '../types';
import { conversationDisplayName } from './today';
import { formatInstantGB } from '../gbDate';

// ------------------------------------------------------------------------------------------------ chat list
export type ChatSegment = 'all' | 'unread' | 'mentions' | 'channels' | 'dms';
export const CHAT_SEGMENTS: { id: ChatSegment; label: string }[] = [
  { id: 'all', label: 'All' }, { id: 'unread', label: 'Unread' }, { id: 'mentions', label: 'Mentions' }, { id: 'channels', label: 'Channels' }, { id: 'dms', label: 'DMs' },
];
type ListConv = Pick<Conversation, 'id' | 'kind' | 'title' | 'slug' | 'members' | 'unread_count' | 'mention_count' | 'notify_level' | 'last_message_at' | 'archived_at'>;
export const isUnread = (c: Pick<Conversation, 'unread_count' | 'notify_level'>) => c.unread_count > 0 && c.notify_level !== 'none';
export interface ChatRow<C> { c: C; name: string; unread: boolean; badge: { n: number; tone: 'mention' | 'unread' } | null; muted: boolean }
/** The phone's one chat list: latest activity first, filtered by segment and a name search. Closed rooms are left out. */
export function chatListRows<C extends ListConv>(convs: C[], o: { me?: string | null; segment?: ChatSegment; query?: string }): ChatRow<C>[] {
  const seg = o.segment ?? 'all';
  const q = (o.query ?? '').trim().toLowerCase();
  return convs
    .filter(c => !(c.kind === 'room' && c.archived_at))
    .filter(c => seg === 'all' || (seg === 'unread' ? isUnread(c) || c.mention_count > 0 : seg === 'mentions' ? c.mention_count > 0 : seg === 'channels' ? c.kind === 'channel' : c.kind === 'dm' || c.kind === 'group_dm'))
    .map(c => ({
      c, name: conversationDisplayName(c, o.me), unread: isUnread(c) || c.mention_count > 0, muted: c.notify_level === 'none',
      badge: c.mention_count > 0 ? { n: c.mention_count, tone: 'mention' as const } : isUnread(c) && c.kind !== 'channel' ? { n: c.unread_count, tone: 'unread' as const } : null,
    }))
    .filter(r => !q || r.name.toLowerCase().includes(q))
    .sort((a, b) => (b.c.last_message_at ?? '').localeCompare(a.c.last_message_at ?? '') || a.name.localeCompare(b.name));
}

// ------------------------------------------------------------------------------------------------ t193u: people in chat search
export interface ChatPersonMatch<M> { member: M; dmId: string | null }
/**
 * Searching the chat list also finds people (active members, not me), whether or not we have talked: each opens our DM
 * when there is one (dmId) or starts it. A DM that a person row stands for is not listed again (hiddenDmIds).
 */
export function chatSearchPeople<M extends { user_id: string; display_name: string; job_title?: string | null; email?: string | null; status?: string }>(
  members: M[], convs: Pick<Conversation, 'id' | 'kind' | 'members'>[], me: string | null | undefined, query: string, limit = 20,
): { people: ChatPersonMatch<M>[]; hiddenDmIds: Set<string> } {
  const q = query.trim().toLowerCase();
  const hiddenDmIds = new Set<string>();
  if (!q) return { people: [], hiddenDmIds };
  const dmWith = new Map<string, string>();
  for (const c of convs) {
    if (c.kind !== 'dm') continue;
    const other = c.members?.find(m => m.user_id !== me) ?? (c.members?.length === 1 ? c.members[0] : undefined);
    if (other) dmWith.set(other.user_id, c.id);
  }
  const words = q.split(/\s+/);
  const people = members
    .filter(m => m.user_id !== me && (m.status ?? 'active') === 'active')
    .filter(m => { const h = `${m.display_name} ${m.job_title ?? ''} ${m.email ?? ''}`.toLowerCase(); return words.every(w => h.includes(w)); })
    .sort((a, b) => Number(dmWith.has(b.user_id)) - Number(dmWith.has(a.user_id)) || a.display_name.localeCompare(b.display_name))
    .slice(0, limit)
    .map(m => ({ member: m, dmId: dmWith.get(m.user_id) ?? null }));
  for (const p of people) if (p.dmId) hiddenDmIds.add(p.dmId);
  return { people, hiddenDmIds };
}

// ------------------------------------------------------------------------------------------------ threads
/** The single line a thread shows as in the timeline: "2 replies · last 23:04". */
export function threadLine(replyCount: number, lastReplyAt: string | null | undefined, time: (iso: string) => string): string {
  const n = `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`;
  return lastReplyAt ? `${n} · last ${time(lastReplyAt)}` : n;
}

// ------------------------------------------------------------------------------------------------ long-press sheet
/**
 * t543u: who sees Mark complete / Reopen on a form post, on the web, the phone web and the iOS app. It mirrors the API
 * (MessagesService.setCompleted): the poster, channel managers (per-channel `manage`, which includes owners and
 * workspace-wide managers), people with forms.post.set_status in the channel, and, for a post with a linked journey,
 * people who can plan journeys. Only top-level, undeleted form posts. The server checks again either way.
 */
export function canCompleteFormPost(
  m: { sender_id: string | null; parent_id?: string | null; deleted_at?: string | null; form?: unknown; metadata?: unknown; linked_journey?: unknown },
  who: { userId: string | null | undefined; canSetStatus?: boolean; manage?: boolean; canPlanJourneys?: boolean },
): boolean {
  const isForm = Boolean(m.form || (m.metadata as { form?: unknown } | null | undefined)?.form);
  if (!isForm || m.parent_id || m.deleted_at) return false;
  return (Boolean(who.userId) && m.sender_id === who.userId) || Boolean(who.canSetStatus) || Boolean(who.manage)
    || (Boolean(m.linked_journey) && Boolean(who.canPlanJourneys));
}

export type MessageSheetAction = 'reply' | 'copy' | 'forward' | 'task' | 'ack' | 'translate' | 'pin' | 'link' | 'edit' | 'delete' | 'journey' | 'complete' | 'report';
export const MESSAGE_SHEET_LABEL: Record<MessageSheetAction, string> = {
  reply: 'Reply in thread', copy: 'Copy text', forward: 'Forward', task: 'Make task', ack: 'Require acknowledgement', translate: 'Translate',
  pin: 'Pin', link: 'Copy link', edit: 'Edit', delete: 'Delete', journey: 'Plan delivery', complete: 'Mark complete', report: 'Report',
};
/** What the long-press sheet offers for a message, in order (§4.6.3). */
export function messageSheetActions(m: { mine: boolean; kind: string; deleted: boolean; hasText: boolean; isPoll?: boolean; taskComment?: boolean },
  can: { reply: boolean; forward: boolean; ack: boolean; translate: boolean; journey: boolean; inThread?: boolean; /** t369u: false with Tasks off. */ task?: boolean;
    /** t520u: the channel's permissions (absent = as before: pin for everyone, edit and delete your own). */ pin?: boolean; edit?: boolean; delete?: boolean;
    /** t518u: Mark complete / Reopen a form post. */ complete?: boolean;
    /** t536u: false where reporting isn't offered. */ report?: boolean }): MessageSheetAction[] {
  if (m.deleted || m.kind !== 'user') return [];
  const out: MessageSheetAction[] = [];
  if (can.reply) out.push('reply');
  if (m.hasText) out.push('copy');
  if (can.forward) out.push('forward');
  if (can.task !== false) out.push('task');
  if (can.ack) out.push('ack');
  if (can.translate) out.push('translate');
  if (can.pin !== false) out.push('pin');
  out.push('link');
  if (can.journey) out.push('journey');
  if (can.complete) out.push('complete');
  if (m.mine && !m.isPoll && !m.taskComment && can.edit !== false) out.push('edit');
  if (can.delete ?? m.mine) out.push('delete');
  // t536u: anyone else's message can be reported (last, away from the everyday actions).
  if (!m.mine && can.report !== false) out.push('report');
  return out;
}

// ------------------------------------------------------------------------------------------------ layout
/** How much of the layout viewport the on-screen keyboard covers (0 when it's closed). */
export function keyboardInset(layoutHeight: number, visualHeight: number, visualOffsetTop = 0): number {
  const inset = Math.round(layoutHeight - visualHeight - visualOffsetTop);
  return inset > 80 ? inset : 0;
}
/** Conversations and threads are full screen on the phone: no top bar or tab bar, the header has a back button. */
export const isFullScreenChat = (path: string) => /^\/c\/[^/]+\/?$/.test(path);

// ------------------------------------------------------------------------------------------------ unsent messages
/**
 * An unsent message is retried by itself (when the connection returns) only when the failure was the connection or the
 * server, never a refusal (4xx: not allowed, too long…), which needs the person. The server dedupes on
 * client_message_id, so a retry can't post twice.
 */
export function isRetryableSendError(e: unknown): boolean {
  const status = (e as { status?: unknown } | null)?.status;
  if (typeof status !== 'number' || status === 0) return true; // fetch failed: offline, DNS, dropped
  return status >= 500 || status === 408 || status === 429;
}
export const MAX_AUTO_RETRIES = 5;
export function shouldAutoRetry(p: { status: 'sending' | 'failed'; retryable?: boolean; attempts?: number }): boolean {
  return p.status === 'failed' && p.retryable !== false && (p.attempts ?? 0) < MAX_AUTO_RETRIES;
}

/** The chat list's time column: "14:05" today, "Yesterday", "Mon" this week, then "12 Oct". */
export function chatTime(iso: string | null | undefined, now: Date = new Date(), tz = 'Europe/London'): string {
  if (!iso) return '';
  const d = new Date(iso);
  const day = (x: Date) => formatInstantGB(x, 'yyyy-MM-dd', tz);
  const days = Math.round((Date.parse(day(now)) - Date.parse(day(d))) / 86_400_000);
  if (days <= 0) return formatInstantGB(d, 'HH:mm', tz);
  if (days === 1) return 'Yesterday';
  if (days < 7) return formatInstantGB(d, 'ccc', tz);
  return formatInstantGB(d, 'd LLL', tz);
}
